import React, { useState } from 'react';
import { DialogContent, DialogActions, Stack, TextField, Button, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import { IconButton } from '@mui/material';
import { Columns } from '@/types';
import { AllAssemblyTemplatesForFilterSideBarDocument, SortTypeEnum, TableCellStyleEnum, TemplateFieldTypeEnum, TemplateTypeEnum, useUpdateTemplateMutation } from '@/__generated__/graphql';
import { GenericModal } from '@/components/shared/GenericModal';
import { dialogOverlay } from '../NewComponent/styles';
import { formatEnumForDisplay, LowerSnakeCase, mapDisplayTypeToColumnType } from '@/utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAtomValue } from 'jotai';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { useSelectedAssemblyTemplateId } from '../../hooks';
import { Delete as DeleteIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import * as analysisState from '@/components/Analysis/state';
import { prepareTemplateFields } from '@/hooks/usePrepareTemplateFields';

type Props = {
  open: boolean;
  initialColumns: Columns[]
  onClose: () => void;
  onCreate: (col: Columns) => void;
};

export const ColumnModal = ({ open, onClose, onCreate, initialColumns }: Props) => {
  const [newColumnName, setNewColumnName] = useState('');
  const structureId = useGetStructureId()
  const [displayType, setDisplayType] = useState<TableCellStyleEnum>(TableCellStyleEnum.Text);
  const analysisAssemblyTemplate = useAtomValue(analysisState.assemblyTemplate);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [dropdownOptions, setDropdownOptions] = useState<string[]>(['']);
  const [addNewColumn] = useUpdateTemplateMutation({
    onCompleted: (response) => {
      if (!response?.updateTemplate) {
        showSnackBar('An error occurred while adding column', 'error');
        return;
      }
      showSnackBar('Columns added successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });
  const handleAddDropdownOption = () => setDropdownOptions((d) => [...d, '']);
  const handleRemoveDropdownOption = (index: number) => setDropdownOptions((d) => d.filter((_, i) => i !== index));
  const handleChangeDropdownOption = (index: number, value: string) =>
    setDropdownOptions((d) => d.map((v, i) => (i === index ? value : v)));

  const handleCreate = () => {
    if (!newColumnName) return;
    const key = LowerSnakeCase(newColumnName);
    const mappedType = mapDisplayTypeToColumnType(displayType);

    const newColumn = {
      name: newColumnName,
      key: `planner_${key}`,
      type: mappedType,
      displayConfig: {
        id: generateMongoObjectId(),
        type: displayType,
        sortType: mappedType !== TemplateFieldTypeEnum.Numeric ? SortTypeEnum.String : SortTypeEnum.Number,
        isSortable: true
      },
      options: displayType === TableCellStyleEnum.Dropdown ? dropdownOptions.filter(Boolean) : undefined,
      displayInQuery: displayType === TableCellStyleEnum.Dropdown ? dropdownOptions.filter(Boolean).length > 0 : false,
      enableInlineEdit: true,
      serverSide: false,
    };

    const { cleanedFields, preventPlannerDuplicateField } = prepareTemplateFields(analysisAssemblyTemplate?.fields);

    if (preventPlannerDuplicateField({ ...newColumn, columnName: newColumnName, isSortable: false, visibility: true }, initialColumns)) {
      showSnackBar('Column already Exists', 'error');
      return;
    }

    onCreate({
      ...newColumn, columnName: newColumnName, isSortable: false, visibility: true, templateFieldKey: `planner_${key}`,
      isTemplateField: true,
    });

    addNewColumn({
      variables: {
        input: {
          type: TemplateTypeEnum.Assembly,
          structureId,
          templateId: selectedAssemblyTemplateId!,
          fields: [...cleanedFields, newColumn],
          lastModifiedFor: 'Update Fields',
        },
      },
      refetchQueries: [AllAssemblyTemplatesForFilterSideBarDocument]
    }).catch((err) => {
      console.error(err);
    });
    setNewColumnName('');
    setDisplayType(TableCellStyleEnum.Text);
    setDropdownOptions(['']);
    onClose();
  };

  return (
    <GenericModal
      open={open}
      handleClose={onClose}
      title="New column"
      description={
        'Use custom columns to capture additional information in your table. Choose a column name and select the type of values it will contain.'
      }
      titleSx={{ fontWeight: 600 }}
      backgroundPaperStyle={{
        minHeight: '40rem',
        display: 'flex',
        flexDirection: 'column',
      }}
      sx={{ ...dialogOverlay, height: 'fit-content' }}
    >
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', overflowY: 'auto', height: '100%', minHeight: 0 }}>
        <Stack spacing={2} sx={{ width: '50rem' }}>
          <TextField label="Column name" value={newColumnName} onChange={(e) => setNewColumnName(e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <Stack spacing={2} direction='row' sx={{ width: '60rem' }}>
              <FormControl fullWidth>
                <InputLabel id="display-type-label">Display type</InputLabel>
                <Select
                  labelId="display-type-label"
                  value={displayType}
                  label="Display type"
                  onChange={(e) => setDisplayType(e.target.value as TableCellStyleEnum)}
                >
                  {[
                    TableCellStyleEnum.Date,
                    TableCellStyleEnum.Dropdown,
                    TableCellStyleEnum.Number,
                    TableCellStyleEnum.Text,
                  ].map((val) => (
                    <MenuItem key={val} value={val}>
                      {formatEnumForDisplay(val)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {displayType === TableCellStyleEnum.Dropdown && (
            <Box sx={{ maxHeight: '28rem', overflowY: 'auto', pr: 1 }}>
              {dropdownOptions.map((opt, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TextField placeholder={`Value ${String(idx + 1).padStart(2, '0')}`} value={opt} onChange={(e) => handleChangeDropdownOption(idx, e.target.value)} fullWidth />
                  <IconButton onClick={() => handleRemoveDropdownOption(idx)} aria-label="remove-option">
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
              <Button onClick={handleAddDropdownOption} startIcon={<AddIcon />}>Add option</Button>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 5 }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate}>Confirm</Button>
      </DialogActions>
    </GenericModal>
  );

};

